import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  defaultKeyOf,
  KeyOf,
  MasterdataEntityDefinition,
  MasterdataEntityRegistry,
  MasterdataInputComponent,
} from '@softapps/core/masterdata';
import {
  ButtonComponent,
  DsChipComponent,
  IconComponent,
  InputComponent,
  OverlayComponent,
  SoftSlotDirective,
  TranslatePipe,
} from '@softline/ui-core';
import { SOFTLINE_AI_AGENT_MENTION_ENTITIES } from '../../ai-agent.tokens';
import { mentionToolArgument } from '../../functions/mention-tool-argument';
import { findActiveMention, replaceRange } from '../../functions/mention-parser';
import { ChatMention } from '../../types/chat-mention.type';

/** Which half of the picker an open mention shows. */
type MentionStage = 'entity' | 'search';

/**
 * Chat input with `@`-mentions of masterdata. `@` opens a two-step picker in an
 * overlay anchored to this host: first the entity (Kunde / Artikel /
 * Produktgruppe), then the real masterdata search (`soft-masterdata-input` in
 * `inline` mode), so mentions get the same bookmarks, paging, settings, table
 * view and error handling as every other masterdata field.
 *
 * The overlay is an `soft-ds-overlay` rather than a `ContextMenuService` menu —
 * that one closes on any outside click, which fights typing.
 */
@Component({
  selector: 'soft-ai-agent-composer',
  templateUrl: './composer.component.html',
  styleUrls: ['./composer.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    InputComponent,
    ButtonComponent,
    IconComponent,
    SoftSlotDirective,
    TranslatePipe,
    OverlayComponent,
    DsChipComponent,
    MasterdataInputComponent,
  ],
})
export class AiAgentComposerComponent {
  private readonly registry = inject(MasterdataEntityRegistry);
  private readonly entityNames = inject(SOFTLINE_AI_AGENT_MENTION_ENTITIES);
  protected readonly host = inject(ElementRef);

  readonly disabled = input(false);
  readonly compact = input(false);
  readonly submitted = output<{ text: string; mentions: ChatMention[] }>();

  protected readonly draft = signal<string | null>(null);
  protected readonly mentions = signal<ChatMention[]>([]);
  private readonly caret = signal(0);

  private readonly inputComponent = viewChild(InputComponent);
  private readonly masterdataInput = viewChild(MasterdataInputComponent);

  protected readonly entities = computed(() =>
    this.registry.all().filter((d) => this.entityNames.includes(d.name))
  );

  /** The `@…` token under the caret; `null` closes the picker. */
  protected readonly active = computed(() =>
    findActiveMention(this.draft() ?? '', this.caret())
  );

  /** The entity the user picked, reset whenever a different mention opens. */
  private readonly chosenEntity = linkedSignal<number | null, string | null>({
    source: () => this.active()?.start ?? null,
    computation: () => null,
  });

  /** Highlighted row of the entity step; the draft input drives it by keyboard. */
  protected readonly highlighted = linkedSignal<string | null, number>({
    source: () => this.chosenEntity(),
    computation: () => 0,
  });

  /**
   * Which masterdata entity the picker searches: an explicit `@entity:` prefix
   * wins, then the entity the user chose. `null` means: still choosing.
   */
  protected readonly activeEntityName = computed<string | null>(() => {
    const mention = this.active();
    if (!mention) return null;
    const entities = this.entities();
    if (mention.entity && entities.some((d) => d.name === mention.entity))
      return mention.entity;
    const chosen = this.chosenEntity();
    return chosen && entities.some((d) => d.name === chosen) ? chosen : null;
  });

  protected readonly stage = computed<MentionStage | null>(() => {
    if (!this.active()) return null;
    return this.activeEntityName() ? 'search' : 'entity';
  });

  private readonly activeDefinition =
    computed<MasterdataEntityDefinition<unknown> | null>(() => {
      const name = this.activeEntityName();
      return name ? this.registry.find(name) : null;
    });

  protected readonly activeEntityLabel = computed(
    () => this.activeDefinition()?.label ?? ''
  );

  /**
   * Single-element list so `@for`'s `track` recreates the search when the entity
   * changes: `soft-masterdata-input` seeds and runs its inline search once on
   * render, so reusing the instance across entities would keep stale results.
   */
  protected readonly activeEntityNames = computed<string[]>(() => {
    const name = this.activeEntityName();
    return name ? [name] : [];
  });

  /** Whatever was typed after the `@`; seeds the masterdata search. */
  protected readonly searchSeed = computed(() => this.active()?.term || null);

  protected readonly open = linkedSignal(() => this.active() !== null);

  protected syncCaret(): void {
    const element = this.inputComponent()?.inputElement()?.nativeElement;
    if (element) this.caret.set(element.selectionStart ?? 0);
  }

  /**
   * The entity step has no focus of its own — the draft input keeps it so the
   * user can keep typing — so its arrow keys and Enter are handled here. In the
   * search step the inline masterdata search owns the keyboard.
   */
  protected onKeydown(event: KeyboardEvent): void {
    if (this.stage() !== 'entity') {
      if (event.key === 'Enter') this.submit();
      return;
    }
    const entities = this.entities();
    if (entities.length === 0) return;
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        event.preventDefault();
        const delta = event.key === 'ArrowDown' ? 1 : -1;
        this.highlighted.update((i) => (i + delta + entities.length) % entities.length);
        return;
      }
      case 'Enter':
      case 'Tab': {
        const entity = entities[this.highlighted()];
        if (!entity) return;
        event.preventDefault();
        this.selectEntity(entity.name);
        return;
      }
      case 'Escape':
        event.preventDefault();
        this.closePicker();
        return;
    }
  }

  /** Step 1 to step 2: open the masterdata search with the focus in its field. */
  protected selectEntity(name: string): void {
    this.chosenEntity.set(name);
    // `autoFocus` alone is not enough: a seeded term makes the search hand the
    // focus to the result table, and the field is where typing continues.
    setTimeout(() => this.masterdataInput()?.focus());
  }

  protected backToEntities(): void {
    this.chosenEntity.set(null);
    this.focusInput();
  }

  /** Turns a picked masterdata record into a chip plus `@<label>` in the draft. */
  protected applyRecord(dto: unknown): void {
    const mention = this.active();
    const definition = this.activeDefinition();
    if (!mention || !definition) return;

    const key = String(keyAccessor(definition)(dto) ?? '');
    const label = definition.displayWith(dto);
    const replacement = `@${label} `;
    this.writeDraft(
      replaceRange(this.draft() ?? '', mention.start, mention.end, replacement),
      mention.start + replacement.length
    );

    const id = `${definition.name}:${key}`;
    this.mentions.update((list) =>
      list.some((m) => m.id === id)
        ? list
        : [
            ...list,
            {
              id,
              entity: definition.name,
              key,
              label,
              toolArgument: mentionToolArgument(definition.name, key),
            },
          ]
    );
    this.chosenEntity.set(null);
  }

  /** The inline search has no dismiss of its own, so the overlay owns one. */
  protected closePicker(): void {
    this.open.set(false);
    this.chosenEntity.set(null);
    this.focusInput();
  }

  protected removeMention(id: string): void {
    this.mentions.update((list) => list.filter((m) => m.id !== id));
  }

  protected submit(): void {
    const text = (this.draft() ?? '').trim();
    if (text.length === 0 || this.disabled()) return;
    this.submitted.emit({ text, mentions: this.mentions() });
    this.draft.set(null);
    this.mentions.set([]);
    this.caret.set(0);
    this.chosenEntity.set(null);
  }

  private writeDraft(text: string, caret: number): void {
    this.draft.set(text);
    this.caret.set(caret);
    // The new value reaches the DOM with the next change detection; moving the
    // caret before that would put it back where the old value ended.
    setTimeout(() => {
      const element = this.inputComponent()?.inputElement()?.nativeElement;
      if (!element) return;
      element.focus();
      element.setSelectionRange(caret, caret);
    });
  }

  private focusInput(): void {
    setTimeout(() => this.inputComponent()?.inputElement()?.nativeElement.focus());
  }
}

function keyAccessor(definition: MasterdataEntityDefinition<unknown>): KeyOf<unknown> {
  return (definition.keyWith ?? defaultKeyOf) as KeyOf<unknown>;
}
